import { validateMapPhysicsAsset } from "./validate-map-asset.js";
const SIMULATION_VERSION = 2;
export const detonationRules = {
    FLASH: "fuse",
    HE: "fuse",
    SMOKE: "rest",
    DECOY: "rest",
    MOLLY: "impact",
};
/** Obergrenze für Granaten, die erst liegend zünden (lange Smokes rollen). */
const REST_DETONATION_LIMIT_SECONDS = 10;
/** Ab dieser Normalen zählt ein Aufprall als Boden (Molly zündet). */
const IMPACT_FLOOR_NORMAL_Z = 0.7;
const DEGREES_TO_RADIANS = Math.PI / 180;
const EPSILON_TIME = 1e-6;
const EPSILON_DISTANCE = 1e-6;
const MAX_COLLISIONS_PER_STEP = 4;
function assertFiniteVector(name, vector) {
    for (const [axis, value] of Object.entries(vector)) {
        if (!Number.isFinite(value)) {
            throw new RangeError(`${name}.${axis} must be finite`);
        }
    }
}
function validateInputs(intent, ruleset) {
    assertFiniteVector("intent.origin", intent.origin);
    assertFiniteVector("intent.target", intent.target);
    assertFiniteVector("intent.playerVelocity", intent.playerVelocity);
    if (!Number.isFinite(intent.pitchDegrees)) {
        throw new RangeError("intent.pitchDegrees must be finite");
    }
    if (!Number.isFinite(intent.yawDegrees)) {
        throw new RangeError("intent.yawDegrees must be finite");
    }
    if (!Number.isFinite(intent.strength) ||
        intent.strength < 0 ||
        intent.strength > 1) {
        throw new RangeError("intent.strength must be between 0 and 1");
    }
    if (!Number.isFinite(ruleset.gravityHuPerSecondSquared) ||
        ruleset.gravityHuPerSecondSquared <= 0) {
        throw new RangeError("ruleset.gravityHuPerSecondSquared must be positive");
    }
    if (!Number.isFinite(ruleset.sampleIntervalSeconds) ||
        ruleset.sampleIntervalSeconds <= 0) {
        throw new RangeError("ruleset.sampleIntervalSeconds must be positive");
    }
    if (!Number.isFinite(ruleset.maxFlightTimeSeconds) ||
        ruleset.maxFlightTimeSeconds <= 0) {
        throw new RangeError("ruleset.maxFlightTimeSeconds must be positive");
    }
    const fuseSeconds = ruleset.fuseSeconds[intent.grenadeType];
    const throwSpeed = ruleset.throwSpeedHuPerSecond[intent.mode];
    if (!Number.isFinite(fuseSeconds) || fuseSeconds <= 0) {
        throw new RangeError(`ruleset.fuseSeconds.${intent.grenadeType} must be positive`);
    }
    if (!Number.isFinite(throwSpeed) || throwSpeed < 0) {
        throw new RangeError(`ruleset.throwSpeedHuPerSecond.${intent.mode} must not be negative`);
    }
}
export function initialThrowVelocity(intent, ruleset) {
    validateInputs(intent, ruleset);
    const pitch = intent.pitchDegrees * DEGREES_TO_RADIANS;
    const yaw = intent.yawDegrees * DEGREES_TO_RADIANS;
    const speed = ruleset.throwSpeedHuPerSecond[intent.mode] * intent.strength;
    const horizontalSpeed = Math.cos(pitch) * speed;
    return {
        x: Math.cos(yaw) * horizontalSpeed + intent.playerVelocity.x,
        y: Math.sin(yaw) * horizontalSpeed + intent.playerVelocity.y,
        z: Math.sin(pitch) * speed + intent.playerVelocity.z,
    };
}
function sampleAt(timeSeconds, origin, initialVelocity, gravity) {
    return {
        timeSeconds,
        position: {
            x: origin.x + initialVelocity.x * timeSeconds,
            y: origin.y + initialVelocity.y * timeSeconds,
            z: origin.z +
                initialVelocity.z * timeSeconds -
                0.5 * gravity * timeSeconds * timeSeconds,
        },
        velocity: {
            x: initialVelocity.x,
            y: initialVelocity.y,
            z: initialVelocity.z - gravity * timeSeconds,
        },
    };
}
function addVectors(a, b) {
    return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
function subtractVectors(a, b) {
    return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}
function scaleVector(vector, scalar) {
    return {
        x: vector.x * scalar,
        y: vector.y * scalar,
        z: vector.z * scalar,
    };
}
function dot(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
function magnitude(vector) {
    return Math.hypot(vector.x, vector.y, vector.z);
}
function normalize(vector) {
    const length = magnitude(vector);
    if (length <= EPSILON_DISTANCE)
        return null;
    return scaleVector(vector, 1 / length);
}
function planePositionAt(origin, velocity, gravity, timeSeconds) {
    return {
        x: origin.x + velocity.x * timeSeconds,
        y: origin.y + velocity.y * timeSeconds,
        z: origin.z +
            velocity.z * timeSeconds -
            0.5 * gravity * timeSeconds * timeSeconds,
    };
}
function velocityAt(initialVelocity, gravity, timeSeconds) {
    return {
        x: initialVelocity.x,
        y: initialVelocity.y,
        z: initialVelocity.z - gravity * timeSeconds,
    };
}
function solveRootsInInterval(a, b, c, maxTime) {
    if (Math.abs(a) <= EPSILON_DISTANCE) {
        if (Math.abs(b) <= EPSILON_DISTANCE)
            return [];
        const root = -c / b;
        return root >= 0 && root <= maxTime ? [root] : [];
    }
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0)
        return [];
    const discriminantSqrt = Math.sqrt(discriminant);
    return [(-b - discriminantSqrt) / (2 * a), (-b + discriminantSqrt) / (2 * a)]
        .filter((candidate) => candidate >= 0 && candidate <= maxTime)
        .sort((left, right) => left - right);
}
function classifyFloorOrCeilingNormal(kind) {
    if (kind === "floor")
        return { x: 0, y: 0, z: 1 };
    if (kind === "ceiling")
        return { x: 0, y: 0, z: -1 };
    return null;
}
function createCollisionPlanes(mapAsset) {
    const planes = [];
    const materialById = new Map(mapAsset.materials.map((material) => [material.id, material]));
    for (const surface of mapAsset.surfaces) {
        const [a, b, c] = surface.vertices;
        const explicitNormal = classifyFloorOrCeilingNormal(surface.kind);
        let normal = explicitNormal;
        if (!normal && surface.kind === "wall") {
            const ab = subtractVectors(b, a);
            const ac = subtractVectors(c, a);
            const cross = {
                x: ab.y * ac.z - ab.z * ac.y,
                y: ab.z * ac.x - ab.x * ac.z,
                z: ab.x * ac.y - ab.y * ac.x,
            };
            normal = normalize(cross);
        }
        if (!normal)
            continue;
        const materialResponse = materialById.get(surface.materialId);
        planes.push({
            kind: surface.kind,
            surfaceId: surface.id,
            materialId: surface.materialId,
            point: a,
            normal,
            vertices: surface.vertices,
            ...(materialResponse ? { materialResponse } : {}),
        });
    }
    return planes;
}
function compactTrajectoryBounds(origin, velocity, gravity, intervalSeconds, radius) {
    const end = planePositionAt(origin, velocity, gravity, intervalSeconds);
    let highestZ = Math.max(origin.z, end.z);
    const apexTime = velocity.z / gravity;
    if (apexTime > 0 && apexTime < intervalSeconds) {
        highestZ = Math.max(highestZ, planePositionAt(origin, velocity, gravity, apexTime).z);
    }
    return {
        minimum: {
            x: Math.min(origin.x, end.x) - radius,
            y: Math.min(origin.y, end.y) - radius,
            z: Math.min(origin.z, end.z) - radius,
        },
        maximum: {
            x: Math.max(origin.x, end.x) + radius,
            y: Math.max(origin.y, end.y) + radius,
            z: highestZ + radius,
        },
    };
}
function findCompactCollisionHit(environment, origin, velocity, gravity, intervalSeconds, radius) {
    const { minimum, maximum } = compactTrajectoryBounds(origin, velocity, gravity, intervalSeconds, radius);
    const values = new Float64Array(9);
    let earliestTime = Number.POSITIVE_INFINITY;
    let earliest;
    for (const range of environment.collision.queryRanges(minimum, maximum)) {
        const endTriangle = range.startTriangle + range.triangleCount;
        for (let index = range.startTriangle; index < endTriangle; index += 1) {
            const groupIndex = environment.collision.readTriangleCoordinates(index, values);
            const ax = values[0];
            const ay = values[1];
            const az = values[2];
            const abx = values[3] - ax;
            const aby = values[4] - ay;
            const abz = values[5] - az;
            const acx = values[6] - ax;
            const acy = values[7] - ay;
            const acz = values[8] - az;
            let nx = aby * acz - abz * acy;
            let ny = abz * acx - abx * acz;
            let nz = abx * acy - aby * acx;
            const normalLength = Math.hypot(nx, ny, nz);
            if (normalLength <= EPSILON_DISTANCE)
                continue;
            nx /= normalLength;
            ny /= normalLength;
            nz /= normalLength;
            const aAlongNormal = -gravity * nz;
            const bAlongNormal = nx * velocity.x + ny * velocity.y + nz * velocity.z;
            const distanceAtStart = (origin.x - ax) * nx + (origin.y - ay) * ny + (origin.z - az) * nz;
            const roots = [
                ...solveRootsInInterval(0.5 * aAlongNormal, bAlongNormal, distanceAtStart - radius, Math.min(intervalSeconds, earliestTime)),
                ...solveRootsInInterval(0.5 * aAlongNormal, bAlongNormal, distanceAtStart + radius, Math.min(intervalSeconds, earliestTime)),
            ].sort((left, right) => left - right);
            for (const candidateTime of roots) {
                const centerPosition = planePositionAt(origin, velocity, gravity, candidateTime);
                const distance = (centerPosition.x - ax) * nx +
                    (centerPosition.y - ay) * ny +
                    (centerPosition.z - az) * nz;
                const side = distance >= 0 ? 1 : -1;
                const cnx = nx * side;
                const cny = ny * side;
                const cnz = nz * side;
                if (velocity.x * cnx +
                    velocity.y * cny +
                    (velocity.z - gravity * candidateTime) * cnz >=
                    -EPSILON_DISTANCE) {
                    continue;
                }
                const px = centerPosition.x - cnx * radius - ax;
                const py = centerPosition.y - cny * radius - ay;
                const pz = centerPosition.z - cnz * radius - az;
                const dot00 = acx * acx + acy * acy + acz * acz;
                const dot01 = acx * abx + acy * aby + acz * abz;
                const dot02 = acx * px + acy * py + acz * pz;
                const dot11 = abx * abx + aby * aby + abz * abz;
                const dot12 = abx * px + aby * py + abz * pz;
                const denominator = dot00 * dot11 - dot01 * dot01;
                if (Math.abs(denominator) <= EPSILON_DISTANCE)
                    continue;
                const u = (dot11 * dot02 - dot01 * dot12) / denominator;
                const v = (dot00 * dot12 - dot01 * dot02) / denominator;
                if (u < -EPSILON_DISTANCE ||
                    v < -EPSILON_DISTANCE ||
                    u + v > 1 + EPSILON_DISTANCE) {
                    continue;
                }
                earliestTime = candidateTime;
                earliest = {
                    index,
                    groupIndex,
                    centerPosition,
                    normal: { x: cnx, y: cny, z: cnz },
                    planeNormal: { x: nx, y: ny, z: nz },
                    vertices: [
                        { x: ax, y: ay, z: az },
                        { x: values[3], y: values[4], z: values[5] },
                        { x: values[6], y: values[7], z: values[8] },
                    ],
                };
                break;
            }
        }
    }
    if (!earliest)
        return null;
    const group = environment.collision.header.groups[earliest.groupIndex];
    return {
        timeSeconds: earliestTime,
        centerPosition: earliest.centerPosition,
        normal: earliest.normal,
        plane: {
            kind: Math.abs(earliest.planeNormal.z) >= 0.5 ? "floor" : "wall",
            surfaceId: `compact:${earliest.index}`,
            materialId: group.surfaceProperty,
            point: earliest.vertices[0],
            normal: earliest.planeNormal,
            vertices: earliest.vertices,
            ...(group.materialResponse
                ? { materialResponse: group.materialResponse }
                : {}),
        },
    };
}
function pointIsInsideTriangle(point, [a, b, c]) {
    const edge0 = subtractVectors(c, a);
    const edge1 = subtractVectors(b, a);
    const relativePoint = subtractVectors(point, a);
    const dot00 = dot(edge0, edge0);
    const dot01 = dot(edge0, edge1);
    const dot02 = dot(edge0, relativePoint);
    const dot11 = dot(edge1, edge1);
    const dot12 = dot(edge1, relativePoint);
    const denominator = dot00 * dot11 - dot01 * dot01;
    if (Math.abs(denominator) <= EPSILON_DISTANCE)
        return false;
    const inverseDenominator = 1 / denominator;
    const u = (dot11 * dot02 - dot01 * dot12) * inverseDenominator;
    const v = (dot00 * dot12 - dot01 * dot02) * inverseDenominator;
    return (u >= -EPSILON_DISTANCE &&
        v >= -EPSILON_DISTANCE &&
        u + v <= 1 + EPSILON_DISTANCE);
}
function signedDistanceToPlane(position, plane) {
    return dot(subtractVectors(position, plane.point), plane.normal);
}
function findCollisionHit(origin, velocity, gravity, intervalSeconds, radius, planes) {
    let earliestHit = null;
    for (const plane of planes) {
        const aAlongNormal = dot(plane.normal, { x: 0, y: 0, z: -gravity });
        const bAlongNormal = dot(plane.normal, velocity);
        const distanceAtStart = signedDistanceToPlane(origin, plane);
        const candidateRoots = [
            ...solveRootsInInterval(0.5 * aAlongNormal, bAlongNormal, distanceAtStart - radius, intervalSeconds),
            ...solveRootsInInterval(0.5 * aAlongNormal, bAlongNormal, distanceAtStart + radius, intervalSeconds),
        ].sort((left, right) => left - right);
        for (const candidateTime of candidateRoots) {
            const contactPosition = planePositionAt(origin, velocity, gravity, candidateTime);
            const contactDistance = signedDistanceToPlane(contactPosition, plane);
            const candidateNormal = contactDistance >= 0 ? plane.normal : scaleVector(plane.normal, -1);
            if (dot(velocityAt(velocity, gravity, candidateTime), candidateNormal) >=
                -EPSILON_DISTANCE) {
                continue;
            }
            const contactPoint = subtractVectors(contactPosition, scaleVector(candidateNormal, radius));
            if (!pointIsInsideTriangle(contactPoint, plane.vertices))
                continue;
            const candidate = {
                timeSeconds: candidateTime,
                centerPosition: contactPosition,
                normal: candidateNormal,
                plane,
            };
            if (!earliestHit || candidate.timeSeconds < earliestHit.timeSeconds) {
                earliestHit = candidate;
            }
            break;
        }
    }
    return earliestHit;
}
function resolveCollisionVelocity(incomingVelocity, normal, ruleset, materialResponse) {
    const restitution = materialResponse?.restitution ?? ruleset.restitution;
    const friction = materialResponse?.friction ?? ruleset.friction;
    let adjustedNormal = normal;
    let normalSpeed = dot(incomingVelocity, adjustedNormal);
    if (normalSpeed > 0) {
        adjustedNormal = scaleVector(adjustedNormal, -1);
        normalSpeed = dot(incomingVelocity, adjustedNormal);
    }
    const normalComponent = scaleVector(adjustedNormal, normalSpeed);
    const tangentComponent = subtractVectors(incomingVelocity, normalComponent);
    const bouncedNormal = scaleVector(adjustedNormal, -restitution * normalSpeed);
    const dampedTangent = scaleVector(tangentComponent, Math.max(0, 1 - friction));
    return addVectors(bouncedNormal, dampedTangent);
}
function simulateWithoutCollisions(intent, ruleset, initialVelocity, durationSeconds) {
    const samples = [
        sampleAt(0, intent.origin, initialVelocity, ruleset.gravityHuPerSecondSquared),
    ];
    for (let step = 1; step * ruleset.sampleIntervalSeconds < durationSeconds; step += 1) {
        samples.push(sampleAt(step * ruleset.sampleIntervalSeconds, intent.origin, initialVelocity, ruleset.gravityHuPerSecondSquared));
    }
    samples.push(sampleAt(durationSeconds, intent.origin, initialVelocity, ruleset.gravityHuPerSecondSquared));
    return {
        simulationVersion: SIMULATION_VERSION,
        ruleset: { id: ruleset.id, version: ruleset.version },
        mapAsset: null,
        samples,
        collisions: [],
        endReason: durationSeconds === ruleset.fuseSeconds[intent.grenadeType]
            ? "fuse"
            : "max_time",
        durationSeconds,
        finalPosition: { ...samples[samples.length - 1].position },
    };
}
export function simulateGrenadeThrow(intent, ruleset, environment = {}) {
    const initialVelocity = initialThrowVelocity(intent, ruleset);
    const fuseSeconds = ruleset.fuseSeconds[intent.grenadeType];
    const rule = detonationRules[intent.grenadeType];
    const flightSeconds = Math.min(fuseSeconds, ruleset.maxFlightTimeSeconds);
    const mapAsset = environment.mapAsset;
    const compactMapAsset = environment.compactMapAsset;
    if (mapAsset && compactMapAsset) {
        throw new RangeError("simulation environment must contain only one map asset");
    }
    if (!mapAsset && !compactMapAsset) {
        return simulateWithoutCollisions(intent, ruleset, initialVelocity, flightSeconds);
    }
    if (compactMapAsset &&
        compactMapAsset.collision.header.mapId !== compactMapAsset.reference.mapId) {
        throw new RangeError("compact collision mapId does not match its reference");
    }
    if (mapAsset)
        validateMapPhysicsAsset(mapAsset);
    const collisionPlanes = mapAsset ? createCollisionPlanes(mapAsset) : null;
    if (collisionPlanes?.length === 0) {
        return simulateWithoutCollisions(intent, ruleset, initialVelocity, flightSeconds);
    }
    // Ohne Geometrie kann nichts liegen bleiben; mit Geometrie fliegt eine
    // liegend zündende Granate so lange, bis sie ruht.
    const durationSeconds = rule === "rest" ? REST_DETONATION_LIMIT_SECONDS : flightSeconds;
    const gravity = ruleset.gravityHuPerSecondSquared;
    const samples = [
        {
            timeSeconds: 0,
            position: { ...intent.origin },
            velocity: { ...initialVelocity },
        },
    ];
    const collisions = [];
    let currentTime = 0;
    let position = { ...intent.origin };
    let velocity = { ...initialVelocity };
    const endReason = rule !== "rest" && durationSeconds === fuseSeconds ? "fuse" : "max_time";
    const mapReference = () => compactMapAsset?.reference ?? {
        mapId: mapAsset.mapId,
        mapRevision: mapAsset.mapRevision,
        assetVersion: mapAsset.assetVersion,
    };
    while (currentTime < durationSeconds - EPSILON_TIME) {
        const remainingTotal = durationSeconds - currentTime;
        const intervalSeconds = Math.min(ruleset.sampleIntervalSeconds, remainingTotal);
        let consumedSeconds = 0;
        let collisionCount = 0;
        while (consumedSeconds < intervalSeconds - EPSILON_TIME) {
            if (collisionCount > MAX_COLLISIONS_PER_STEP)
                break;
            const remainingStepSeconds = intervalSeconds - consumedSeconds;
            const hit = compactMapAsset
                ? findCompactCollisionHit(compactMapAsset, position, velocity, gravity, remainingStepSeconds, ruleset.grenadeRadiusHu)
                : findCollisionHit(position, velocity, gravity, remainingStepSeconds, ruleset.grenadeRadiusHu, collisionPlanes);
            if (!hit) {
                position = planePositionAt(position, velocity, gravity, remainingStepSeconds);
                velocity = velocityAt(velocity, gravity, remainingStepSeconds);
                break;
            }
            if (hit.timeSeconds > EPSILON_TIME) {
                position = planePositionAt(position, velocity, gravity, hit.timeSeconds);
                velocity = velocityAt(velocity, gravity, hit.timeSeconds);
                consumedSeconds += hit.timeSeconds;
            }
            else {
                consumedSeconds += EPSILON_TIME;
            }
            const incomingVelocity = { ...velocity };
            const outgoingVelocity = resolveCollisionVelocity(incomingVelocity, hit.normal, ruleset, hit.plane.materialResponse);
            collisions.push({
                timeSeconds: currentTime + consumedSeconds,
                position: { ...position },
                normal: { ...hit.normal },
                surfaceId: hit.plane.surfaceId,
                materialId: hit.plane.materialId,
                incomingVelocity,
                outgoingVelocity,
            });
            samples.push({
                timeSeconds: currentTime + consumedSeconds,
                position: { ...position },
                velocity: { ...outgoingVelocity },
            });
            velocity = outgoingVelocity;
            position = addVectors(position, scaleVector(hit.normal, EPSILON_DISTANCE));
            collisionCount += 1;
            if (rule === "impact" && hit.normal.z >= IMPACT_FLOOR_NORMAL_Z) {
                return {
                    simulationVersion: SIMULATION_VERSION,
                    ruleset: { id: ruleset.id, version: ruleset.version },
                    mapAsset: mapReference(),
                    samples,
                    collisions,
                    endReason: "impact",
                    durationSeconds: currentTime + consumedSeconds,
                    finalPosition: { ...position },
                };
            }
            if (hit.normal.z >= 0.5 &&
                magnitude(velocity) <= ruleset.stopSpeedHuPerSecond) {
                currentTime += consumedSeconds;
                const restingSeconds = rule === "fuse"
                    ? Math.max(currentTime, durationSeconds)
                    : currentTime;
                // Eine Flash/HE, die vor der Zündzeit liegt, zündet trotzdem erst
                // nach Ablauf – am Liegeort.
                if (restingSeconds > currentTime)
                    samples.push({
                        timeSeconds: restingSeconds,
                        position: { ...position },
                        velocity: { x: 0, y: 0, z: 0 },
                    });
                return {
                    simulationVersion: SIMULATION_VERSION,
                    ruleset: { id: ruleset.id, version: ruleset.version },
                    mapAsset: mapReference(),
                    samples,
                    collisions,
                    endReason: rule === "fuse" ? endReason : "stopped",
                    durationSeconds: restingSeconds,
                    finalPosition: { ...position },
                };
            }
        }
        currentTime += intervalSeconds;
        samples.push({
            timeSeconds: currentTime,
            position: { ...position },
            velocity: { ...velocity },
        });
    }
    return {
        simulationVersion: SIMULATION_VERSION,
        ruleset: { id: ruleset.id, version: ruleset.version },
        mapAsset: mapReference(),
        samples,
        collisions,
        endReason,
        durationSeconds,
        finalPosition: { ...samples[samples.length - 1].position },
    };
}
